import { useEffect, useState } from 'react'
import type { BackgroundWorkStatus } from '@/frontend/features/AgentChats/events/backgroundWork'

// The server owns the lease. Expiration only withdraws the claim that work is
// running; it never cancels work or fabricates a successful/failed outcome.
export function executionStatus(status: BackgroundWorkStatus, validUntil: string | null | undefined, now: number): BackgroundWorkStatus {
  if (!['starting', 'running'].includes(status) || !validUntil) return status
  const deadline = Date.parse(validUntil)
  return !Number.isFinite(deadline) || now >= deadline ? 'unknown' : status
}

export function useExecutionStatus(status: BackgroundWorkStatus, validUntil?: string | null): BackgroundWorkStatus {
  const [now, setNow] = useState(Date.now)
  useEffect(() => {
    setNow(Date.now())
    if (!validUntil || !['starting', 'running'].includes(status)) return
    const remaining = Date.parse(validUntil) - Date.now()
    if (!Number.isFinite(remaining) || remaining <= 0) return
    const timer = window.setTimeout(() => setNow(Date.now()), Math.min(remaining + 1, 2_147_483_647))
    // Timers may be throttled while hidden; verify again on foreground/reconnect.
    const refresh = () => setNow(Date.now())
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('online', refresh)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', refresh)
      window.removeEventListener('online', refresh)
    }
  }, [status, validUntil])
  return executionStatus(status, validUntil, Math.max(now, Date.now()))
}
